import * as Sentry from '@sentry/vue'
import axios, { AxiosResponse, AxiosError } from 'axios'
import { useRefreshToken } from '@/composable/RequestUtils/useAuthorization'
import { useAuthStore } from '@/composable/StoreUtils/useAuthStore'
import { useNotification } from '@/composable/useNotification'
import { AxiosErrorExtended } from '@/config/ts-definitions'
import { translate } from '@/i18n'
import { AUTH } from '@/settings/RequestErrors'
import { useSelector } from '@/store/rootStore'

// Create Instances
const api = axios.create()

let isLoggingOut = false
export const setLoggingOut = () => {
	isLoggingOut = true
	setTimeout(() => {
		isLoggingOut = false
	}, 1000)
}

// Headers
export const setAuthHeader = (token: string) => {
	api.defaults.headers.common['Authorization'] = token
}

export const removeAuthHeader = () => {
	delete api.defaults.headers.common['Authorization']
	setLoggingOut()
}

export const setBaseUrl = (baseUrl: string) => {
	api.defaults.baseURL = baseUrl
}

const failSomeRequestsTesting = window.location.hash.includes(
	'ServicesFailSomeRequestsTesting'
)

// Interceptors
api.interceptors.response.use(
	(response: AxiosResponse) => response,
	(error: AxiosErrorExtended) => {
		// wait for the possible custom message
		setTimeout(() => {
			// Skip error notifications if we're in a password reset flow
			const isPasswordResetFlow =
				window.location.pathname === '/auth/verify' ||
				window.location.pathname === '/auth/error' ||
				localStorage.getItem('resetPasswordToken')

			if (isPasswordResetFlow) {
				return
			}

			if (isLoggingOut) {
				return
			}

			const notificationState = useSelector((state) => state.notification)
			const notificationHandlers = useNotification()
			const shouldShowNotification = !error.suppressNotification
			if (
				(error.response?.status && error.response.status >= 500) ||
				(!!error.customMessage && !notificationState.value.open)
			) {
				const customMessage = error.customMessage
					? (failSomeRequestsTesting ? 'FAILTEST: ' : '') + error.customMessage
					: 'Something went wrong'
				const customMessageDelay = error.customMessageDelay ?? 8000
				const customMessageClosable = error.customMessageClosable ?? true
				const extendedCustomMessage = error.customMessage
					? `${customMessage}. ${translate(
						'common.PLEASE_TRY_AGAIN_OR_CONTACT_SUPPORT'
					)}`
					: `${customMessage}. ${translate(
						'common.TRY_REFRESHING_OR_CONTACT_SUPPORT'
					)}`

				if (shouldShowNotification) {
					notificationHandlers.enqueueNotification({
						color: 'danger',
						message: extendedCustomMessage,
						closable: customMessageClosable,
						delay: customMessageDelay,
					})
				}

				Sentry.withScope((scope) => {
					scope.setTags({
						'axios-error': true,
						'axios-error-status': error.response?.status,
					})
					scope.setContext('Request Details', {
						method: error.config?.method,
						url: error.config?.url,
						status: error.response?.status,
						data: error.config?.data ?? {},
						headers: error.config?.headers,
						response: error.response?.data,
					})

					// wrap the error to use the custom message
					// and keep the original error stack
					const wrappedError: any = new Error(extendedCustomMessage)
					wrappedError.stack = error.stack
					wrappedError.name = 'Axios: ' + customMessage
					wrappedError.original = error

					const singleSetupData = useSelector((state) => {
						return state.singleSetup
					})

					if (singleSetupData.value.data?.user?.id) {
						scope.setUser({
							id: singleSetupData.value.data?.user?.id,
							email: singleSetupData.value.data?.user?.email,
							username: singleSetupData.value.data?.user?.username,
							'First Name': singleSetupData.value.data?.user?.firstname,
							'Last Name': singleSetupData.value.data?.user?.lastname,
						})
					}

					Sentry.captureException(wrappedError)
				})
			}
		}, 100)

		return Promise.reject(error)
	}
)

const interceptorsInstance = {
	refreshToken: {} as { [key: string]: unknown },
}
export const setRefreshTokenInterceptor = () => {
	interceptorsInstance.refreshToken.ref = api.interceptors.response.use(
		(response: AxiosResponse) => {
			interceptorsInstance.refreshToken.retried = false
			return response
		},
		async (error: AxiosError) => {
			if (error.response && AUTH.includes(error.response.status)) {
				// Skip token refresh if we are in a password reset flow
				const isPasswordResetFlow =
					window.location.pathname === '/auth/verify' ||
					window.location.pathname === '/auth/error' ||
					localStorage.getItem('resetPasswordToken')

				if (isPasswordResetFlow) {
					return Promise.reject(error)
				}

				if (isLoggingOut) {
					return Promise.reject(error)
				}

				const { config } = error
				const { setNewAuthorization, removeAuthorization } = useAuthStore()
				const { userToken, refreshToken, requestToken } = useRefreshToken()
				if (
					!interceptorsInstance.refreshToken.retried &&
					localStorage.getItem('refreshToken')
				) {
					interceptorsInstance.refreshToken.retried = true
					await requestToken(localStorage.getItem('refreshToken') as string)
					setNewAuthorization(userToken.value, refreshToken.value)
					if (config.headers) {
						config.headers['Authorization'] = userToken.value
					}
					return api(config)
				} else {
					removeAuthorization()
					location.reload()
				}
			}
			return Promise.reject(error)
		}
	)
}

export const removeRefreshTokenInterceptor = () => {
	if (typeof interceptorsInstance.refreshToken.ref === 'number') {
		api.interceptors.response.eject(interceptorsInstance.refreshToken.ref)
	}
}

export default api
